import dayjs from 'dayjs';
import i18next from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import * as yup from 'yup';
import { useCustomers } from '../../api/hooks/customers/useCustomers';
import { useProductForm } from '../../api/hooks/products/useProductForm';
import { useSale } from '../../api/hooks/sales/useSale';
import { useForm } from '../../api/hooks/useForm';
import { DataPlaceholder } from '../../components/atoms/DataPlaceholder';
import { Loading } from '../../components/atoms/Loading';
import { DocForm } from '../../components/molecules/docForm/DocForm';
import { patternTwoDigitsAfterComma, patternTwoDigitsAfterCommaSigned } from '../../config/utils/calcUtils';
import { dateAsYMD, getDueDate, nowDateInUtc } from '../../config/utils/dateHelpers';
import { defaultTaxWithhold } from '../../config/utils/salesHelper';
import { PosSettings } from '../../interfaces/company.interface';
import {
  Customer,
  CUSTOMER_TYPE_AADE_CODE,
  CUSTOMER_VAT_REGIME_AADE_CODE,
  CustomerVatRegime,
} from '../../interfaces/customer.interface';
import {
  DOC_TYPE_AADE_CODE,
  DocumentTypeInterface,
  INVOICE_AADE_CODES,
  INVOICE_TO_PERSON_AADE_CODES,
} from '../../interfaces/documentType.interface';
import { SUBSCRIPTION } from '../../interfaces/enumerables';
import { EntityFormInterface, INPUT_TYPE } from '../../interfaces/forms.interface';
import { DocPurpose, InvoiceInterface } from '../../interfaces/invoice.interface';
import { RECEIVING_NOTE_PURPOSE } from '../../interfaces/myData.interface';
import { PAYMENT_METHOD_AADE_CODE, PaymentType } from '../../interfaces/paymentTypes.interface';
import { PATHS } from '../../navigation/data';
import { emptyProductObject } from '../../services/utils/data';
import { getDocPurposesWithDocType, getDocPurposesWithoutDocTypeValue } from '../../services/utils/docPurposeUtils';
import {
  isCorrelationRequired,
  isDocTypeDeliveryNote,
  isDocTypeInvertedDeliveryNote,
  isQuantityReceiptForm,
  isQuantityReceiptFormRelated,
} from '../../services/utils/myDataUtils';
import { useHasActivePlan } from '../../services/utils/useHasActivePlan';
import { images } from '../../theme/assets/img';

const mapCustomerData = (selectedEntity: Customer | undefined, data?: any[]) => {
  if (selectedEntity) {
    return [
      {
        label: selectedEntity?.companyName,
        value: selectedEntity?.id,
        object: selectedEntity,
      },
    ];
  }

  if (data) {
    return data.map((entity) => {
      return {
        label: entity?.companyName,
        value: entity?.id,
        object: entity,
      };
    });
  }

  return null;
};

const defaultValueReceipt = (
  docTypes: DocumentTypeInterface[],
  selectedDocTypeId?: number | null,
  mostUsedDocumentTypeId?: number | null,
) => {
  if (selectedDocTypeId) {
    const found = docTypes.find((ent) => ent.id === selectedDocTypeId);

    return {
      label: `(${found?.aadeCode}) ` + i18next.t(`DOC_TYPES.${found?.name}`),
      value: found?.id,
      object: found,
    };
  }

  // Default new docs to whichever doc type the company actually issues the most, when available
  // (it may not be — e.g. a company on a lower subscription tier than when it issued that type).
  // Falls back to the first entry (server-sorted by aadeId) otherwise.
  const mostUsed = mostUsedDocumentTypeId ? docTypes.find((ent) => ent.id === mostUsedDocumentTypeId) : undefined;
  const docType = mostUsed || docTypes[0];
  if (docType) {
    return {
      label: `(${docType?.aadeCode}) ` + i18next.t(`DOC_TYPES.${docType?.name}`),
      value: docType?.id,
      object: docType,
    };
  }

  // Default value in case no list is returned
  return {
    label: i18next.t('SALE.SELECT_DOC_TYPE'),
    value: 7,
    object: {},
  };
};

const defaultPaymentType = (data: PaymentType[]) => {
  const entity = data?.find((doc) => doc.aadeCode === '5');
  return {
    label: i18next.t(`PAYMENT_TYPES.${entity?.name}`),
    value: entity?.id,
    object: entity,
  };
};

const defaultPos = (data: PosSettings[], currentPos: PosSettings | null | undefined) => {
  if (!currentPos) return null;

  const entity = data?.find((pos) => pos.tid === currentPos.tid);
  if (entity) {
    return {
      label: entity?.name,
      value: entity?.tid,
      object: entity,
    };
  }

  return null;
};

const defaultVatRegime = (data: CustomerVatRegime[], initVal?: CustomerVatRegime) => {
  if (initVal) {
    return [
      {
        label: i18next.t(`VAT_REGIME.${initVal?.name}`),
        value: initVal?.id,
        object: initVal,
      },
    ];
  }

  return data?.map((entity) => {
    return {
      label: i18next.t(`VAT_REGIME.${entity?.name}`),
      value: entity?.id,
      object: entity,
    };
  });
};

const defaultValueDocPurpose = (entities: DocPurpose[], entityId?: number) => {
  if (entityId) {
    const found = entities.find((ent) => ent.id === entityId);
    if (found) {
      return [
        {
          label: `(${found?.aadeCode}) ` + i18next.t(`DOC_PURPOSES.${found?.name}`),
          value: found?.id,
          object: found,
        },
      ];
    }
  }
  return entities?.map((entity) => {
    return {
      label: `(${entity?.aadeCode}) ` + i18next.t(`DOC_PURPOSES.${entity?.name}`),
      value: entity?.id,
      object: entity,
    };
  });
};

const prefillForm = (entity: InvoiceInterface | null | undefined, isClone?: boolean) => {
  if (!entity) return null;

  return {
    customer: mapCustomerData(entity?.customer)?.[0] || null,
    vatRegime: entity?.vatRegime,
    vatExceptionCategory: entity?.vatExceptionCategoryId,
    documentType: entity?.documentTypeId,
    seriesName: entity?.seriesName,
    seriesNumber: entity?.serialNumber,
    documentPurpose: entity?.docPurposeId,
    documentPurpose2: entity?.docPurpose2Id,
    documentDate: isClone ? nowDateInUtc() : dateAsYMD(entity?.documentDate || nowDateInUtc()),
    dueDate: getDueDate(entity?.dueDate),
    // Don't default a missing `shipDate` to today here like `documentDate` above - this value also
    // becomes the formik diff baseline (`getChangedFields`/`DocForm.tsx`). A doc saved under a
    // non-delivery doc type (e.g. PRO_FORMA_INVOICE) has no `shipDate` at all; if we defaulted it
    // to "today" anyway, switching to a delivery-relevant doc type (e.g. 1.1) would just land back
    // on the *same* "today" default via `DocPaymentDelivery`'s own effect, so the diff would see no
    // change and silently drop `shipDate` from the update payload. Leaving it `null` here means the
    // switch is a genuine change (`null` -> today's date) that actually gets sent.
    shipDate: isClone ? nowDateInUtc() : entity?.shipDate ? dateAsYMD(entity.shipDate) : null,
    paymentType: entity?.paymentType,
    pos: entity?.pos,
    shipping: entity?.shipping,
    shipFrom: entity?.shipFrom,
    shipTo: entity?.shipTo,
    shipToCity: entity?.shipToCity,
    shipToPostalCode: entity?.shipToPostalCode,
    notes: entity?.notes,
    products: entity?.products,
    taxWithhold: entity?.taxWithhold || null,
    post_with_provider: entity?.post_with_provider || false,
    isDeliveryNote: entity?.id ? entity?.isDeliveryNote || false : true,
    productsToBeWeighed: entity?.productsToBeWeighed || false,
    receivingNotePurpose: entity?.receivingNotePurpose ?? null,
    otherReceivingNotePurposeTitle: entity?.otherReceivingNotePurposeTitle ?? null,
    correlatedInvoices: entity?.correlatedInvoices || [],
    nonObligatedRecipient: entity?.nonObligatedRecipient || false,
    withoutDigitalTransportTracking: entity?.withoutDigitalTransportTracking || false,
  };
};

const getDefaultDeliverToFromValue = (isEditOrClone: boolean, translationKey: string) => {
  if (isEditOrClone) return '';

  return i18next.t(`SALE.${translationKey}`);
};

const getFormData = (
  id?: number | string,
  data?: any,
  entityData?: InvoiceInterface | null | undefined,
  isClone?: boolean,
  isCorrelate?: boolean,
): EntityFormInterface<InvoiceInterface> => {
  const prefillData = prefillForm(entityData, isClone);
  const isEditOrClone = isClone || !!id;
  const simpleDocPurposes = getDocPurposesWithoutDocTypeValue(data?.docPurposes);
  const docPurposesWithDocType = getDocPurposesWithDocType(data?.docPurposes);

  return {
    id,
    fields: [
      {
        name: 'customerId',
        label: 'PICK_CUSTOMER',
        placeholder: 'CUSTOMER',
        value: prefillData?.customer || null,
        fieldValue: 'companyName',
        fieldValue2: 'lastName',
        fieldToSubmit: 'value',
        fieldToSubmit2: 'id',
        fieldToSearch: 'vatNumber',
        // @ts-expect-error happens
        useHook: useCustomers,
        required: true,
        type: INPUT_TYPE.ANTD_SELECT_SEARCH,
      },
      {
        name: 'vatRegimeId',
        label: 'VAT_REGIME',
        placeholder: 'VAT_REGIME',
        value: defaultVatRegime(data.customerVatRegimes || [], prefillData?.vatRegime)[0],
        fieldToSubmit: 'id',
        fieldToSubmit2: 'value',
        labelStyle: 'padding-b-16 padding-l-12',
        translationGroupKey: 'VAT_REGIME',
        data: data?.customerVatRegimes,
        type: INPUT_TYPE.ANTD_SELECT_SEARCH,
      },
      {
        name: 'vatExceptionCategoryId',
        label: 'VAT_EXCEPTION_CAT',
        placeholder: 'VAT_EXCEPTION_CAT',
        value: prefillData?.vatExceptionCategory,
        labelStyle: 'padding-b-16 padding-l-12',
        translationGroupKey: 'VAT_EXCEPTION_CAT',
        fieldToSubmit: 'value',
        data: data?.vatExceptionCategories,
        type: INPUT_TYPE.ANTD_SELECT_SEARCH,
      },
      {
        name: 'documentTypeId',
        label: 'DOCUMENT_TYPE',
        placeholder: 'DOCUMENT_TYPE',
        value: defaultValueReceipt(data?.docTypes || [], entityData?.documentTypeId, data?.mostUsedDocumentTypeId),
        fieldValue: 'name',
        fieldToSubmit: 'value',
        type: INPUT_TYPE.ANTD_SELECT,
        data: data?.docTypes || [],
      },
      {
        name: 'docPurposeId',
        label: 'DOC_PURPOSE',
        placeholder: 'DOC_PURPOSE',
        value: defaultValueDocPurpose(simpleDocPurposes || [], prefillData?.documentPurpose)?.[0],
        fieldValue: 'name',
        fieldToSubmit: 'value',
        type: INPUT_TYPE.ANTD_SELECT,
        required: true,
        labelStyle: 'padding-b-16 padding-l-12',
        labelPrefix: (param: DocPurpose) => `(${param?.aadeCode}) `,
        translationGroupKey: 'DOC_PURPOSES',
        data: simpleDocPurposes || [],
      },
      {
        name: 'documentDate',
        label: 'DOC_DATE',
        placeholder: 'DOC_DATE',
        value: prefillData?.documentDate || nowDateInUtc(),
        type: INPUT_TYPE.DATE,
      },
      {
        name: 'dueDate',
        label: 'DOC_DATE',
        placeholder: 'DOC_DATE',
        value: prefillData?.dueDate || nowDateInUtc(),
        type: INPUT_TYPE.DATE,
      },
      {
        name: 'shipDate',
        label: 'DELIVERY',
        placeholder: 'DELIVERY',
        // Only default to today for a brand-new doc (`prefillData` is `null` - see `prefillForm`).
        // When editing/cloning, use `prefillForm`'s value as-is, including `null` for an existing
        // doc that never had a `shipDate` - see the comment on `shipDate` in `prefillForm` for why
        // defaulting that case to today here would silently break the edit-diff on doc type change.
        value: prefillData ? prefillData.shipDate : nowDateInUtc(),
        type: INPUT_TYPE.DATE,
      },
      {
        name: 'seriesName',
        label: 'SERIES_NAME',
        placeholder: 'SERIES_NAME_PLACEHOLDER',
        value: prefillData?.seriesName || '',
        type: INPUT_TYPE.TEXT,
      },
      {
        name: 'serialNumber',
        label: 'SERIAL_NUMBER',
        placeholder: 'SERIAL_NUMBER',
        value: prefillData?.seriesNumber || '',
        type: INPUT_TYPE.NUMBER,
      },
      {
        name: 'products',
        label: 'PRODUCTS',
        placeholder: 'PRODUCTS',
        value: prefillData?.products || [emptyProductObject],
      },
      {
        name: 'paymentTypeId',
        label: 'PAYMENT_TYPE',
        placeholder: 'PAYMENT_TYPE',
        value: prefillData?.paymentType || defaultPaymentType(data?.paymentTypes),
        fieldValue: 'name',
        fieldToSubmit: 'id',
        fieldToSubmit2: 'value',
        type: INPUT_TYPE.ANTD_SELECT,
        data: data?.paymentTypes || [],
      },
      {
        name: 'shipping',
        label: 'SHIPPING',
        placeholder: 'SHIPPING_PLACEHOLDER',
        value: prefillData?.shipping || '',
        type: INPUT_TYPE.ANTD_SELECT_SEARCH,
        fieldToSubmit: 'value',
        maxLength: 40,
      },
      {
        name: 'shipFrom',
        label: 'DELIVERY_PLACE',
        placeholder: 'DELIVERY_PLACE',
        value: prefillData?.shipFrom || getDefaultDeliverToFromValue(isEditOrClone, 'OUR_PLACE'),
        type: INPUT_TYPE.TEXT,
        maxLength: 198,
      },
      {
        name: 'shipTo',
        label: 'DELIVERY_RECEIVE_ADDRESS',
        placeholder: 'DELIVERY_RECEIVE_ADDRESS',
        value: prefillData?.shipTo || getDefaultDeliverToFromValue(isEditOrClone, 'THEIR_PLACE'),
        type: INPUT_TYPE.TEXT,
        maxLength: 198,
      },
      {
        name: 'notes',
        label: 'PUBLIC_NOTES',
        placeholder: 'PUBLIC_NOTES_DESCRIPTION',
        value: prefillData?.notes || '',
        type: INPUT_TYPE.TEXT_AREA,
      },
      {
        name: 'totalValue',
        label: 'TOTAL_CLEAN_VALUE',
        placeholder: 'TOTAL_CLEAN_VALUE',
        value: entityData?.totalValue || 0,
        type: INPUT_TYPE.B_INPUT,
      },
      {
        name: 'taxWithholdId',
        label: 'TAX_WITHHOLD_LABEL',
        placeholder: 'TAX_WITHHOLD_PLACEHOLDER',
        value: prefillData?.taxWithhold || defaultTaxWithhold(data.taxWithholds || [])[0],
        fieldToSubmit: 'id',
        fieldToSubmit2: 'value',
        labelStyle: 'padding-b-16 padding-l-4',
        translationGroupKey: 'TAX_WITHHOLD',
        data: data?.taxWithholds,
        type: INPUT_TYPE.ANTD_SELECT_SEARCH,
      },
      {
        name: 'shipToCity',
        label: 'DELIVERY_RECEIVE_CITY',
        placeholder: 'DELIVERY_RECEIVE_CITY',
        value: prefillData?.shipToCity || '',
        type: INPUT_TYPE.ANTD_SELECT_SEARCH,
        fieldToSubmit: 'value',
        maxLength: 40,
      },
      {
        name: 'shipToPostalCode',
        label: 'DELIVERY_RECEIVE_POSTAL_CODE',
        placeholder: 'DELIVERY_RECEIVE_POSTAL_CODE',
        value: prefillData?.shipToPostalCode || '',
        type: INPUT_TYPE.ANTD_SELECT_SEARCH,
        fieldToSubmit: 'value',
        maxLength: 40,
      },
      {
        name: 'post_with_provider',
        label: 'PROVIDER_POST',
        placeholder: 'CUSTOMER',
        value: prefillData?.post_with_provider || false,
        required: true,
        type: INPUT_TYPE.TOGGLE,
      },
      {
        name: 'pos',
        label: 'POS_DEVICE',
        placeholder: 'POS_SELECT',
        value: defaultPos(data.pos, prefillData?.pos),
        fieldValue: 'name',
        fieldToSubmit: 'object',
        type: INPUT_TYPE.ANTD_SELECT,
        data: data?.pos || [],
      },
      {
        name: 'docPurpose2Id',
        label: 'INVERTED_DOC_PURPOSE',
        placeholder: 'INVERTED_DOC_PURPOSE',
        value: prefillData?.documentPurpose2
          ? defaultValueDocPurpose(docPurposesWithDocType || [], prefillData?.documentPurpose2)?.[0]
          : null,
        fieldValue: 'name',
        fieldToSubmit: 'value',
        type: INPUT_TYPE.ANTD_SELECT,
        required: true,
        labelStyle: 'padding-b-16 padding-l-12',
        labelPrefix: (param: DocPurpose) => `(${param?.aadeCode}) `,
        translationGroupKey: 'DOC_PURPOSES',
        data: docPurposesWithDocType || [],
        tooltip: 'INVERTED_DOC_PURPOSE',
      },
      {
        name: 'isDeliveryNote',
        label: 'IS_DELIVERY_NOTE',
        placeholder: 'IS_DELIVERY_NOTE',
        value: prefillData?.isDeliveryNote,
        required: true,
      },
      {
        name: 'resetSerialNumber',
        label: 'RESET_SERIAL_NUMBER_TITLE',
        placeholder: 'RESET_SERIAL_NUMBER_DESCRIPTION',
        tooltip: 'RESET_SERIAL_NUMBER_DESCRIPTION',
        value: false,
        type: INPUT_TYPE.TOGGLE,
      },
      {
        name: 'productsToBeWeighed',
        label: 'PRODUCTS_TO_BE_WEIGHED_TITLE',
        placeholder: 'PRODUCTS_TO_BE_WEIGHED_DESCRIPTION',
        tooltip: 'PRODUCTS_TO_BE_WEIGHED_DESCRIPTION',
        value: prefillData?.productsToBeWeighed || false,
        type: INPUT_TYPE.TOGGLE,
      },
      {
        name: 'correlatedInvoices',
        label: 'CORRELATED_INVOICES',
        placeholder: 'CORRELATED_INVOICES',
        // When correlating, seed with the source invoice as the first entry
        value:
          isCorrelate && entityData?.mark
            ? [
                { id: entityData.id, mark: entityData.mark },
                ...(prefillData?.correlatedInvoices?.filter((c: { mark: string }) => c.mark !== entityData.mark) || []),
              ]
            : prefillData?.correlatedInvoices ?? [],
      },
      {
        name: 'receivingNotePurpose',
        label: 'RECEIVING_NOTE_PURPOSE_LABEL',
        placeholder: 'RECEIVING_NOTE_PURPOSE_PLACEHOLDER',
        value: prefillData?.receivingNotePurpose ?? null,
        required: true,
      },
      {
        name: 'otherReceivingNotePurposeTitle',
        label: 'RECEIVING_NOTE_PURPOSE_7',
        placeholder: 'RECEIVING_NOTE_PURPOSE_7',
        value: prefillData?.otherReceivingNotePurposeTitle ?? null,
      },
      {
        name: 'nonObligatedRecipient',
        label: 'NON_OBLIGATED_RECIPIENT',
        placeholder: 'NON_OBLIGATED_RECIPIENT',
        value: prefillData?.nonObligatedRecipient ?? false,
      },
      {
        name: 'withoutDigitalTransportTracking',
        label: 'WITHOUT_DIGITAL_TRANSPORT_TRACKING',
        placeholder: 'WITHOUT_DIGITAL_TRANSPORT_TRACKING',
        value: prefillData?.withoutDigitalTransportTracking ?? false,
      },
    ],
    onlyViewFields: [
      {
        name: 'mark',
        label: 'MARK',
        placeholder: 'MARK',
        value: 'NO',
        type: 'text',
        disabled: true,
      },
      {
        name: 'aadeUID',
        label: 'AADE_UID',
        placeholder: 'AADE_UID',
        value: 'NO',
        type: 'text',
        disabled: true,
      },
      {
        name: 'sentToAadeAt',
        label: 'SENT_TO_AADE',
        placeholder: 'SENT_TO_AADE',
        value: i18next.t('FORMS.SENT_TO_AADE_FALSE'),
        type: 'text',
        disabled: true,
      },
      {
        name: 'createdAt',
        label: 'CREATED_AT',
        placeholder: 'CREATED_AT',
        value: '',
        type: 'text',
        disabled: true,
      },
      {
        name: 'updatedBy',
        label: 'UPDATED_BY',
        placeholder: 'UPDATED_BY',
        value: '',
        type: 'text',
        fieldValue: 'typeOf',
        disabled: true,
      },
      {
        name: 'updatedAt',
        label: 'UPDATED_AT',
        placeholder: 'UPDATED_AT',
        value: '',
        type: 'text',
        disabled: true,
      },
    ],
  };
};

const validationSchema = yup.object({
  post_with_provider: yup.boolean().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  isDeliveryNote: yup.boolean().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  customerId: yup.object().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  vatRegimeId: yup.object().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  vatExceptionCategoryId: yup
    .mixed()
    .when('vatRegimeId', {
      is: (vatRegimeId: any) => vatRegimeId?.object?.aadeCode === CUSTOMER_VAT_REGIME_AADE_CODE.ZERO_VAT,
      then: () => yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: () => yup.mixed().notRequired(),
    })
    .nullable(),
  documentTypeId: yup
    .mixed()
    .required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD'))
    .test('invalid-document-type-aadeCode', i18next.t('FORMS.ERRORS.REQUIRED_FIELD'), function (value: any) {
      const documentAadeCode = value?.object?.aadeCode || value?.aadeCode; // Handle both cases where documentTypeId is the object or wrapped in { object }
      if (!documentAadeCode) return false;

      return true; // Valid otherwise
    })
    .test('invalid-document-type', i18next.t('FORMS.ERRORS.INVALID_DOCUMENT_TYPE'), function (value: any) {
      const { customerId } = this.parent;
      const customer = customerId?.object || customerId; // Handle both cases where customerId is the object or wrapped in { object }
      const documentAadeCode = value?.object?.aadeCode || value?.aadeCode; // Handle both cases where documentTypeId is the object or wrapped in { object }

      if (customer?.customerType?.aadeCode === CUSTOMER_TYPE_AADE_CODE.PERSON) {
        if (INVOICE_AADE_CODES.includes(documentAadeCode)) {
          // Title Deed is an invoice but the counterpart is a person
          if (INVOICE_TO_PERSON_AADE_CODES.includes(documentAadeCode)) {
            return true;
          }

          return false;
        }
      }

      return true; // Valid otherwise
    }),
  docPurposeId: yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  docPurpose2Id: yup
    .mixed()
    .when('documentTypeId', {
      is: (documentTypeId: any) => {
        return isDocTypeInvertedDeliveryNote(documentTypeId?.object?.aadeCode);
      },
      then: () => yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: () => yup.mixed().notRequired(),
    })
    .nullable(),
  products: yup.array().of(
    yup.object().shape({
      name: yup.string().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      description: yup.string().notRequired(),
      quantity: yup
        .number()
        .required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD'))
        .test('quantity-min', i18next.t('FORMS.ERRORS.REQUIRED_FIELD'), function (value) {
          const aadeCode = this.from?.[1]?.value?.documentTypeId?.object?.aadeCode;
          if (isQuantityReceiptFormRelated(aadeCode)) {
            return value !== 0 && patternTwoDigitsAfterCommaSigned.test(String(value));
          }
          return (value ?? 0) >= 0.01 && patternTwoDigitsAfterComma.test(String(value));
        }),
      price: yup
        .number()
        .nullable()
        .test('documentTypeId', i18next.t('FORMS.ERRORS.REQUIRED_FIELD'), function (value) {
          if (isDocTypeDeliveryNote(this.from?.[1]?.value?.documentTypeId?.object?.aadeCode)) {
            return value !== null && Number(value) >= 0 && patternTwoDigitsAfterComma.test(String(value));
          }

          return Number(value) > 0 && patternTwoDigitsAfterComma.test(String(value));
        }),
      discountPercentage: yup
        .number()
        .required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD'))
        .min(0)
        .max(100)
        .test((value) => patternTwoDigitsAfterComma.test(String(value))),
      finalCost: yup.number(),
      vatPercentage: yup.number().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
    }),
  ),
  paymentTypeId: yup.object().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
  pos: yup
    .mixed()
    .nullable()
    .when(['post_with_provider', 'paymentTypeId'], {
      is: (post_with_provider: boolean, paymentTypeId: any) => {
        return (
          post_with_provider === true &&
          (paymentTypeId?.object?.aadeCode === PAYMENT_METHOD_AADE_CODE.POS ||
            paymentTypeId?.aadeCode === PAYMENT_METHOD_AADE_CODE.POS)
        );
      },
      then: () => yup.object().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: () => yup.object().notRequired().nullable(),
    }),
  taxWithholdId: yup.object().notRequired(),
  notes: yup.string().notRequired(),
  totalValue: yup.number(),
  shipFrom: yup
    .string()
    .when(['documentTypeId', 'isDeliveryNote'], {
      is: (documentTypeId: any, isDeliveryNote: any) => {
        return (
          (isDocTypeDeliveryNote(documentTypeId?.object?.aadeCode) || isDeliveryNote) &&
          !isQuantityReceiptForm(documentTypeId?.object?.aadeCode)
        );
      },
      then: () => yup.string().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: () => yup.string().notRequired(),
    })
    .max(198),
  shipTo: yup
    .string()
    .when(['documentTypeId', 'isDeliveryNote'], {
      is: (documentTypeId: any, isDeliveryNote: any) => {
        return (
          (isDocTypeDeliveryNote(documentTypeId?.object?.aadeCode) || isDeliveryNote) &&
          !isQuantityReceiptForm(documentTypeId?.object?.aadeCode)
        );
      },
      then: () => yup.string().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: () => yup.string().notRequired(),
    })
    .max(198),
  shipping: yup
    .mixed()
    .when(['documentTypeId', 'isDeliveryNote'], {
      is: (documentTypeId: any, isDeliveryNote: any) => {
        return (
          (isDocTypeDeliveryNote(documentTypeId?.object?.aadeCode) || isDeliveryNote) &&
          !isQuantityReceiptForm(documentTypeId?.object?.aadeCode)
        );
      },
      then: () => yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: () => yup.mixed().notRequired(),
    })
    .nullable(),
  shipToCity: yup
    .mixed()
    .when(['documentTypeId', 'isDeliveryNote'], {
      is: (documentTypeId: any, isDeliveryNote: any) => {
        return (
          (isDocTypeDeliveryNote(documentTypeId?.object?.aadeCode) || isDeliveryNote) &&
          !isQuantityReceiptForm(documentTypeId?.object?.aadeCode)
        );
      },
      then: () => yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: () => yup.mixed().notRequired(),
    })
    .nullable(),
  shipToPostalCode: yup
    .mixed()
    .when(['documentTypeId', 'isDeliveryNote'], {
      is: (documentTypeId: any, isDeliveryNote: any) => {
        return (
          (isDocTypeDeliveryNote(documentTypeId?.object?.aadeCode) || isDeliveryNote) &&
          !isQuantityReceiptForm(documentTypeId?.object?.aadeCode)
        );
      },
      then: () => yup.mixed().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: () => yup.mixed().notRequired(),
    })
    .nullable(),
  documentDate: yup.date().required(),
  dueDate: yup
    .date()
    .test('Due date - Document date', 'Doc date must be before or same as due date', (value, context) => {
      const documentDate = dayjs(context?.parent?.documentDate || '');
      const dayjsDueDate = dayjs(value || '');

      return !dayjsDueDate.isBefore(documentDate, 'day');
    }),
  shipDate: yup
    .date()
    .notRequired()
    .test('Ship date - Document date', 'Doc date must be before or same as ship date', (value, context) => {
      // shipDate only means anything (is shown/required at all) when the doc is actually
      // delivery-relevant - see `isDateRequired` in `DocPaymentDelivery.tsx`, the same condition.
      // For every other doc type, skip the check entirely rather than run it.
      const isShipDateRelevant =
        (isDocTypeDeliveryNote(context?.parent?.documentTypeId?.object?.aadeCode) || context?.parent?.isDeliveryNote) &&
        !isQuantityReceiptForm(context?.parent?.documentTypeId?.object?.aadeCode);

      if (!isShipDateRelevant) {
        return true;
      }

      const documentDate = dayjs(context?.parent?.documentDate || '');
      const dayjsDueDate = dayjs(value || '');

      return !dayjsDueDate.isBefore(documentDate, 'day');
    }),
  resetSerialNumber: yup.boolean().notRequired(),
  productsToBeWeighed: yup.boolean().notRequired(),
  correlatedInvoices: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().nullable().notRequired(),
        mark: yup.string().required(),
      }),
    )
    .when('documentTypeId', {
      is: (documentTypeId: any) => isCorrelationRequired(documentTypeId?.object?.aadeCode),
      then: (schema) => schema.min(1, i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: (schema) => schema.notRequired(),
    }),
  receivingNotePurpose: yup
    .number()
    .nullable()
    .when('documentTypeId', {
      is: (documentTypeId: any) => isQuantityReceiptForm(documentTypeId?.object?.aadeCode),
      then: () => yup.number().required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD')),
      otherwise: () => yup.number().nullable().notRequired(),
    }),
  otherReceivingNotePurposeTitle: yup
    .string()
    .nullable()
    .when('receivingNotePurpose', {
      is: (val: any) => val === RECEIVING_NOTE_PURPOSE.OTHER,
      then: () =>
        yup
          .string()
          .required(i18next.t('FORMS.ERRORS.REQUIRED_FIELD'))
          .max(149, i18next.t('FORMS.ERRORS.MAX_CHARS', { max: 149 })),
      otherwise: () => yup.string().nullable().notRequired(),
    }),
  // For 1.2 / 1.3 with the delivery-note toggle on, AADE requires that the invoice
  // declares either "non-obligated recipient" or "without digital transport tracking"
  // (or both). The test blocks submission when both are false so the UI error shown
  // by TransitToggles is also reflected in formik.isValid.
  nonObligatedRecipient: yup
    .boolean()
    .nullable()
    .test('transit-at-least-one', i18next.t('FORMS.ERRORS.TRANSIT_AT_LEAST_ONE'), function (value) {
      const { documentTypeId, isDeliveryNote, withoutDigitalTransportTracking } = this.parent;
      const docType = documentTypeId?.object?.aadeCode;
      const isEdgeCase2 =
        (docType === DOC_TYPE_AADE_CODE.INVOICE_INTRA_COMMUNITY_DELIVERIES ||
          docType === DOC_TYPE_AADE_CODE.INVOICE_THIRD_COUNTRIES_DELIVERIES) &&
        isDeliveryNote;
      if (isEdgeCase2) return !!value || !!withoutDigitalTransportTracking;
      return true;
    }),
  withoutDigitalTransportTracking: yup.boolean().nullable(),
});

interface FormState {
  formData: any;
  entityData: InvoiceInterface | null;
}

export const NewSaleScreen = () => {
  // Trigger here to use in `DocProduct`
  useProductForm();
  useHasActivePlan(true, SUBSCRIPTION.SIMPLE_V1);
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const isCorrelate = useMemo(() => pathname.includes(PATHS.CORRELATE_SALE), [pathname]);
  const isClone = useMemo(() => pathname.includes(PATHS.CLONE_SALE) || isCorrelate, [pathname, isCorrelate]);
  const isCreate = useMemo(() => id === undefined, [id]);
  const { data: entityData, isLoading: isLoadingEntity } = useSale(id);
  const { data: formData, isLoading } = useForm();
  const [data, setData] = useState<FormState | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPageDisabled, setIsPageDisabled] = useState(false);
  const entityDataReady = useMemo(() => isCreate || entityData, [isCreate, entityData]);

  useEffect(() => {
    if (formData && entityDataReady) {
      const helpData: FormState = { formData, entityData: null };
      if (id && entityData) {
        helpData.entityData = entityData;
      }

      setData(helpData);

      if (id && entityData?.sentToAade && !isClone) {
        setIsPageDisabled(true);
      } else {
        setIsPageDisabled(false);
      }

      setIsReady(true);
    }
  }, [formData, entityData, entityDataReady, id, isClone]);

  const getDisabledSection = useCallback(() => {
    if (isPageDisabled) {
      return (
        <DataPlaceholder
          imgSource={images.somethingWentWrong}
          msg={'SHARED.UNABLE_TO_EDIT_ALREADY_POSTED_TO_MY_DATA'}
        />
      );
    }
  }, [isPageDisabled]);

  const getMainSection = useCallback(() => {
    if (isReady && !isPageDisabled) {
      const dataForm = getFormData(id, data?.formData, data?.entityData, isClone, isCorrelate);
      return (
        <DocForm data={dataForm} validationSchema={validationSchema} isClone={isClone} isCorrelate={isCorrelate} />
      );
    }
  }, [isReady, isPageDisabled, id, data?.formData, data?.entityData, isClone]);

  const getLoading = useCallback(() => {
    if (isLoading || isLoadingEntity) {
      return <Loading backgroundFull />;
    }
  }, [isLoading, isLoadingEntity]);

  return (
    <div>
      {getLoading()}
      {getMainSection()}
      {getDisabledSection()}
    </div>
  );
};
